import { api } from '../../lib/api';
import type { PreviewLink } from '../../types/previewLink';

// Client-side API for the Ad Preview Mockups Stage 1 feature.
//
// Authed surfaces (used by AM-facing UI):
//   - generatePreviewLink — Build View + RowActionsMenu single button
//   - bulkGeneratePreviewLinks — Master Grid bulk action
//   - revokePreviewLink — Generate modal's Revoke affordance
//   - getPreviewLink — read-back after generate / revoke to refresh the
//                      modal with the canonical server shape
//
// The PUBLIC fetch endpoint (/api/public/preview/:token) is consumed
// by the PreviewPage in src/modules/preview/PreviewPage.tsx; it uses
// a plain `fetch` (no auth header) so it stays out of this module's
// authed `api` helper.

export async function generatePreviewLink(
  rolloutId: string,
  rowId: string,
): Promise<PreviewLink> {
  const res = await api<{ link: PreviewLink }>(
    `/api/rollouts/${rolloutId}/rows/${rowId}/preview-links`,
    { method: 'POST', body: '{}' },
  );
  return res.link;
}

export type BulkPreviewLinksResult = {
  links: PreviewLink[];
  rejected: Array<{ rowId: string; reason: string }>;
};

export async function bulkGeneratePreviewLinks(
  rolloutId: string,
  rowIds: string[],
): Promise<BulkPreviewLinksResult> {
  return api<BulkPreviewLinksResult>(
    `/api/rollouts/${rolloutId}/preview-links/bulk`,
    {
      method: 'POST',
      body: JSON.stringify({ rowIds }),
    },
  );
}

export async function revokePreviewLink(id: string): Promise<PreviewLink> {
  const res = await api<{ link: PreviewLink }>(
    `/api/preview-links/${id}/revoke`,
    { method: 'POST', body: '{}' },
  );
  return res.link;
}

export async function getPreviewLink(id: string): Promise<PreviewLink> {
  const res = await api<{ link: PreviewLink }>(`/api/preview-links/${id}`);
  return res.link;
}

// The franchisee-facing URL the AM shares. Built from window.location.origin
// so it's correct in every environment (local dev, Cloud Run, custom domain).
// The token portion of the URL is the only credential — no need to encode
// the rowId / rolloutId on the URL.
export function previewPublicUrl(token: string): string {
  if (typeof window === 'undefined') {
    // Server-side / test usage. Caller is responsible for passing in an
    // origin if they need one; this fallback path keeps the helper pure.
    return `/preview/${token}`;
  }
  return `${window.location.origin}/preview/${token}`;
}
